import {
	Component,
	ViewChild,
	Inject,
	Optional,
	ElementRef,
	HostListener,
	OnDestroy,
	OnInit,
	AfterViewInit
} from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
	FormlySelectProp,
	FormlyInputProp,
	BaseFormlyStaticProp,
	FormlyCheckProp,
	FormlyRadioProp,
	FormlyDateProp,
	FormlyTextareaProp,
	FormlyHiddenProp,
	FormlyService,
	FormlyCollectorComponent,
	ConversionResult
} from '@nx-ahs/formly/shared';
import { ComponentCanDeactivate } from '@nx-ahs/shared/interfaces';
import { FormControl, FormGroup } from '@angular/forms';
import { BlockType, BuilderService } from '../../services/builder.service';
import { FormlyBuilderApiService } from '../../services/formly-builder-api.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { VanillaJsonEditorComponent } from './components/vanilla-json-editor/vanilla-json-editor.component';
import { JsonBuilderComponent } from './components/json-builder/json-builder.component';
import { ActivatedRoute } from '@angular/router';
import { FormlyLanguage } from '@nx-ahs/shared/models';
import { map, BehaviorSubject, Observable, of, startWith } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SiteStorageService } from '@nx-ahs/site-storage';
import { FormlyBuilderI18nService } from '../../services/formly-builder-i18n.service';
import { MatDialog } from '@angular/material/dialog';
import { FormlyTranslationsDialogComponent } from './dialogs/formly-translations-dialog/formly-translations-dialog.component';
import { ImportFromTripettoDialogComponent } from './components/import-from-tripetto-dialog/import-from-tripetto-dialog.component';

@Component({
	selector: 'nx-ahs-formly-builder',
	templateUrl: './formly-builder.component.html',
	styleUrl: './formly-builder.component.scss',
	standalone: false
})
export class FormlyBuilderComponent extends ComponentCanDeactivate implements OnDestroy, OnInit, AfterViewInit {
	@ViewChild('modelEditor', { static: false, read: VanillaJsonEditorComponent })
	modelEditor: VanillaJsonEditorComponent;

	@ViewChild('fieldEditor', { static: false, read: JsonBuilderComponent })
	fieldEditor: JsonBuilderComponent;

	@ViewChild('formlyCollector', { static: false, read: FormlyCollectorComponent })
	formlyCollector: FormlyCollectorComponent;

	@ViewChild('quickAddInput')
	quickAddInput: ElementRef<HTMLInputElement>;

	public loaded = false;
	public model = {};
	public fields: FormlyFieldConfig[] = [];
	public options = { formState: {} };
	public hasChanges = false;
	public form = new FormGroup({});
	public jsonStr: string = '';
	public modelJsonStr: string = '';
	public content: any;
	public modelContent: any;
	public scriptId: number;
	public jsonFieldsReady = false;
	public previewEnabled = false;
	public isJsonValid = true;
	public currentLanguage = 'en';
	public isTranslating = false;
	public builderMode: 'json' | 'visual' = 'visual'; // Default to Visual mode
	public selectedTabIndex = 0; // 0: Visual Builder, 1: Flow Chart, 2: JSON Editor
	public tabs = [
		{ label: 'Visual Builder', icon: 'view_list' },
		{ label: 'Flow Chart', icon: 'device_hub' },
		{ label: 'JSON Editor', icon: 'code' }
	];
	public scriptInfo: any = null;

	// Quick Add properties
	public quickAddControl = new FormControl('');
	public quickAddBlocks: BlockType[] = [];
	public filteredQuickAddOptions: Observable<BlockType[]>;

	private scriptData = '';
	private originalFields: FormlyFieldConfig[] = [];

	// Undo/Redo history
	private undoStack: string[] = [];
	private redoStack: string[] = [];
	private maxHistorySize = 50;
	private isUndoRedoAction = false;

	constructor(
		private route: ActivatedRoute,
		private formlyService: FormlyService,
		private builderApiService: FormlyBuilderApiService,
		private snackBar: MatSnackBar,
		private storageService: SiteStorageService,
		private i18nService: FormlyBuilderI18nService,
		private dialog: MatDialog,
		private builderService: BuilderService,
		@Optional() @Inject('clientConfig') private clientConfig: BehaviorSubject<any>
	) {
		super();
	}

	@HostListener('window:keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		if (event.ctrlKey && event.key === 'z') {
			event.preventDefault();
			this.undo();
		} else if (event.ctrlKey && event.key === 'y') {
			event.preventDefault();
			this.redo();
		}
	}

	ngOnDestroy(): void {
		this.clearHistory();
	}

	ngOnInit() {
		this.scriptId = this.route?.snapshot?.params['scriptId'];

		this.isPreview().then((isPreview) => {
			this.previewEnabled = isPreview;
		});

		// Load script info and versions for the unified toolbar
		this.loadScriptInfo();

		// Initialize Quick Add
		this.quickAddBlocks = this.builderService.blocks.sort((a, b) => a.name.localeCompare(b.name));
		this.filteredQuickAddOptions = this.quickAddControl.valueChanges.pipe(
			startWith(''),
			map((value) => this.filterQuickAddBlocks(value || ''))
		);
	}

	ngAfterViewInit() {
		// Force refresh here or it will have a cached version
		this.getData(true);
	}

	private loadScriptInfo(): void {
		this.formlyService
			.isReady()
			.then(() => this.formlyService.getScript(this.scriptId))
			.then((result) => {
				this.scriptInfo = result;
			});
	}

	// --- Undo/Redo ---

	get canUndo(): boolean {
		return this.undoStack.length > 0;
	}

	get canRedo(): boolean {
		return this.redoStack.length > 0;
	}

	public undo(): void {
		if (!this.canUndo) return;

		// Push current state to redo stack
		this.redoStack.push(this.scriptData);

		// Pop previous state from undo stack
		const previousState = this.undoStack.pop()!;
		this.isUndoRedoAction = true;
		this.restoreState(previousState);
		this.isUndoRedoAction = false;
	}

	public redo(): void {
		if (!this.canRedo) return;

		// Push current state to undo stack
		this.undoStack.push(this.scriptData);

		// Pop next state from redo stack
		const nextState = this.redoStack.pop()!;
		this.isUndoRedoAction = true;
		this.restoreState(nextState);
		this.isUndoRedoAction = false;
	}

	private restoreState(state: string): void {
		this.scriptData = state;
		const fields = JSON.parse(state);
		this.fields = fields;
		this.content = { json: JSON.parse(JSON.stringify(fields)) };
		if (this.fieldEditor?.editor) {
			this.fieldEditor.editor.set(this.content);
		}
		this.hasChanges = true;
	}

	private recordState(): void {
		if (this.isUndoRedoAction) return;

		this.undoStack.push(this.scriptData);
		if (this.undoStack.length > this.maxHistorySize) {
			this.undoStack.shift();
		}
		// New edit clears redo stack
		this.redoStack = [];
	}

	private clearHistory(): void {
		this.undoStack = [];
		this.redoStack = [];
	}

	override canDeactivate(): boolean {
		return !this.hasChanges;
	}

	public async getData(forceDataRefresh: boolean = false): Promise<any> {
		this.jsonFieldsReady = false;
		this.fields = [];
		this.content = { json: [] };

		let localObjPromise: Promise<any>;
		// Only use preview storage if we are NOT forcing a full data refresh.
		if (!forceDataRefresh) {
			localObjPromise = this.formlyService
				.isReady()
				.then(() => this.storageService.storage.get(this.formlyService.previewKey))
				.then((localStr) => (localStr ? JSON.parse(localStr) : null));
		} else {
			localObjPromise = Promise.resolve(null);
		}

		return localObjPromise
			.then(() => {
				return this.formlyService.getScript(this.scriptId, forceDataRefresh);
			})
			.then((result) => {
				// Extract scriptData and store it as a JSON string.
				const scriptData = (result as any)?.scriptData || [];
				this.scriptData = JSON.stringify(scriptData);
				// Parse the stored scriptData to get the fields array.
				const fields = JSON.parse(this.scriptData);
				const contentFields = JSON.parse(JSON.stringify(fields));

				this.content = { json: contentFields };
				this.fields = fields;
				// Store original fields for language switching
				this.originalFields = JSON.parse(JSON.stringify(fields));

				// Make the fields available via options.formState for interpolation.
				(this.options.formState as any).fields = this.fields;

				this.clearHistory();
			})
			.then(() => {
				this.jsonFieldsReady = true;
			});
	}

	public formlyFormReady($event: { fields: FormlyFieldConfig[]; model: any }): void {
		//this.fields = $event.fields;
		//this.content = {
		//	json: this.fields
		//}
		//this.model = $event.model;
		//this.modelContent = {
		//	json: this.model
		//}
		//this.fieldEditor.editor.set(this.content);
		//this.modelEditor.editor.set(this.modelContent);
		//this.fieldEditor.isReady = true;
	}

	public jsonChanged($event: any) {
		this.recordState();

		try {
			this.scriptData = JSON.stringify($event);
		} catch {
			/* JSON.stringify failed */
		}

		// Clean up null layout properties before assigning to fields
		this.fields = this.sanitizeFields($event);
		this.hasChanges = true;
	}

	/**
	 * Recursively sanitize field configs to fix null layout properties
	 * that cause flex-layout directive errors
	 */
	private sanitizeFields(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
		if (!Array.isArray(fields)) {
			return fields;
		}

		return fields.map((field) => {
			// Fix all null layout property values that cause flex-layout errors
			if (field.props?.layout) {
				// fxLayoutGap: null causes ".split() on null" errors
				if (field.props.layout.fxLayoutGap === null) {
					field.props.layout.fxLayoutGap = '0px';
				}
				// fxLayoutAlign: null also causes flex-layout errors
				if ((field.props.layout as any).fxLayoutAlign === null) {
					(field.props.layout as any).fxLayoutAlign = undefined;
				}
				// fxFlex: null also causes flex-layout errors
				if (field.props.layout.fxFlex === null) {
					field.props.layout.fxFlex = undefined;
				}
				// Clean up fxLayoutAlignHorizontal/Vertical (legacy properties)
				if ((field.props.layout as any).fxLayoutAlignHorizontal === null) {
					delete (field.props.layout as any).fxLayoutAlignHorizontal;
				}
				if ((field.props.layout as any).fxLayoutAlignVertical === null) {
					delete (field.props.layout as any).fxLayoutAlignVertical;
				}
			}

			// Recursively sanitize fieldGroup
			if (field.fieldGroup) {
				field.fieldGroup = this.sanitizeFields(field.fieldGroup);
			}

			// Recursively sanitize fieldArray (it's a FormlyFieldConfig itself)
			if (field.fieldArray && typeof field.fieldArray === 'object') {
				this.sanitizeFields([field.fieldArray as FormlyFieldConfig]);
			}

			return field;
		});
	}

	public modelJsonChanged($event: any) {
		this.model = $event;
		//	this.jsonStr = JSON.stringify($event);
		//	let json = JSON.parse(this.jsonStr);

		//	this.content = this.jsonStr;

		//	this.fields = json;

		//	this.hasChanges = true;
	}

	public async preview(): Promise<void> {
		if (!this.previewEnabled) {
			const localObj = {
				scriptId: this.scriptId
			};

			await this.storageService.storage.set(this.formlyService.previewKey, localObj);
			this.previewEnabled = true;
		} else {
			await this.storageService.storage.remove(this.formlyService.previewKey);
			this.previewEnabled = false;
		}
	}

	private async isPreview(): Promise<boolean> {
		const localStr = await this.storageService.storage.get(this.formlyService.previewKey);
		let localObj;
		if (localStr) {
			localObj = JSON.parse(localStr);
			return localObj.scriptId == this.scriptId;
		} else {
			return false;
		}
	}

	private isSaving = false;
	public async save(): Promise<any> {
		// Defensive check: ensures saving is blocked even if the button is triggered programmatically or the UI state is out of sync.
		if (!this.isJsonValid) {
			this.snackBar.open('Cannot save: JSON is invalid. Please fix syntax errors first.', 'x', {
				duration: 3000
			});
			return;
		}

		if (!this.isSaving) {
			this.isSaving = true;

			const params = {
				scriptData: this.scriptData,
				scriptId: this.scriptId
			};

			return this.builderApiService
				.saveScriptFields(params as any)
				.then((result: any) => {
					if (result?.isUpdated) {
						this.snackBar.open('Successfully Saved', 'x', { duration: 3000 });
					}
					return result;
				})
				.then((result: any) => {
					return this.getData(true).then(() => {
						this.hasChanges = false;
						return result;
					});
				})
				.finally(() => {
					this.isSaving = false;
				})
				.catch((error) => {
					this.snackBar.open(`An error occurred: ${error?.message}`, 'x');
					console.error(error);
				});
		}
	}

	public onJsonValidityChanged(isValid: boolean) {
		this.isJsonValid = isValid;
	}

	public modelChanged($event) {
		//this.model = $event;
		//this.modelContent = JSON.stringify($event);

		this.modelEditor.editor.set({ json: $event });
	}

	public onManageTranslations(): void {
		// Get translation languages from client config or use defaults
		let translationLanguages: FormlyLanguage[] = [
			{ code: 'en', name: 'English' },
			{ code: 'es', name: 'Spanish' },
			{ code: 'fr', name: 'French' },
			{ code: 'de', name: 'German' },
			{ code: 'zh', name: 'Chinese' }
		];

		try {
			const config = this.clientConfig?.getValue();
			if (config?.script?.formly?.translationLanguages) {
				translationLanguages = config.script.formly.translationLanguages;
			}
		} catch (error) {
			console.warn('Could not load translation languages from client config, using defaults', error);
		}

		const dialogRef = this.dialog.open(FormlyTranslationsDialogComponent, {
			width: '1400px',
			maxWidth: '95vw',
			data: {
				fields: this.fields,
				languages: translationLanguages
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result?.saved && result?.hasChanges) {
				// Update JSON editor to reflect changes
				this.updateJsonEditor();
				this.hasChanges = true;
				this.snackBar.open('Translations saved successfully!', 'OK', { duration: 3000 });
			}
		});
	}

	private updateJsonEditor(): void {
		// Clean the fields to remove Formly runtime properties before serializing
		const cleanedFields = this.cleanFormlyFields(this.fields);
		this.scriptData = JSON.stringify(cleanedFields);
		const contentFields = JSON.parse(JSON.stringify(cleanedFields));
		this.content = { json: contentFields };
		if (this.fieldEditor?.editor) {
			this.fieldEditor.editor.set(this.content);
		}
	}

	/**
	 * Recursively clean Formly fields by removing runtime properties added by Formly
	 */
	private cleanFormlyFields(fields: FormlyFieldConfig[]): any[] {
		return fields.map((field) => {
			const cleaned: any = {};

			// Core properties to keep
			if (field.key !== undefined) cleaned.key = field.key;
			if (field.type !== undefined) cleaned.type = field.type;
			if (field.props !== undefined || field.templateOptions !== undefined) {
				const props = field.props || field.templateOptions;
				cleaned.props = this.cleanProps(props);
			}
			if (field.hideExpression !== undefined) cleaned.hideExpression = field.hideExpression;
			if (field.className !== undefined) cleaned.className = field.className;
			if (field.defaultValue !== undefined) cleaned.defaultValue = field.defaultValue;

			// Recursively clean nested fields
			if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
				cleaned.fieldGroup = this.cleanFormlyFields(field.fieldGroup);
			}
			if (field.fieldArray && typeof field.fieldArray === 'object') {
				const fieldArray: any = field.fieldArray;
				if (fieldArray.fieldGroup && Array.isArray(fieldArray.fieldGroup)) {
					cleaned.fieldArray = {
						...fieldArray,
						fieldGroup: this.cleanFormlyFields(fieldArray.fieldGroup)
					};
				} else {
					cleaned.fieldArray = fieldArray;
				}
			}

			// Keep other custom properties that aren't Formly runtime additions
			// Exclude: id, hooks, modelOptions, validation, wrappers (if empty), expressions, expressionProperties
			Object.keys(field).forEach((key) => {
				if (
					!Object.prototype.hasOwnProperty.call(cleaned, key) &&
					key !== 'id' &&
					key !== 'hooks' &&
					key !== 'modelOptions' &&
					key !== 'validation' &&
					key !== 'wrappers' &&
					key !== 'expressionProperties' &&
					key !== 'templateOptions' &&
					key !== 'fieldGroup' &&
					key !== 'fieldArray'
				) {
					cleaned[key] = field[key];
				}
			});

			return cleaned;
		});
	}

	/**
	 * Clean props/templateOptions by removing Formly runtime additions
	 */
	private cleanProps(props: any): any {
		if (!props) return props;

		const cleaned = { ...props };

		// Remove isFormField if it exists (added by Formly custom types at runtime)
		if (Object.prototype.hasOwnProperty.call(cleaned, 'isFormField')) {
			delete cleaned.isFormField;
		}

		// Convert empty strings back to null for placeholder/label if they were originally null
		// (Formly sometimes converts null to empty string)
		if (cleaned.placeholder === '') {
			cleaned.placeholder = null;
		}
		if (cleaned.label === '') {
			cleaned.label = null;
		}

		return cleaned;
	}

	public switchMode(mode: 'json' | 'visual'): void {
		if (this.builderMode !== mode) {
			this.builderMode = mode;

			// Sync data when switching modes
			if (mode === 'visual') {
				// Switching to visual: ensure fields are up to date
				try {
					if (this.scriptData) {
						this.fields = JSON.parse(this.scriptData);
					}
				} catch (error) {
					console.error('Error parsing script data for visual mode:', error);
				}
			} else {
				// Switching to JSON: update JSON editor with current fields
				this.updateJsonEditor();
			}
		}
	}

	public onTabChange(tabIndex: number): void {
		// 0: Visual Builder, 1: Flow Chart, 2: JSON Editor
		switch (tabIndex) {
			case 0: // Visual Builder
				this.switchMode('visual');
				break;
			case 1: // Flow Chart
				// Flow chart uses visual mode data
				this.switchMode('visual');
				break;
			case 2: // JSON Editor
				this.switchMode('json');
				break;
		}
	}

	public onVisualBuilderFieldsChanged(): void {
		this.recordState();
		// Update scriptData and JSON editor when visual builder changes fields
		this.updateJsonEditor();
		this.hasChanges = true;
	}

	public async editScriptInfo(): Promise<any> {
		if (!this.scriptInfo) return;

		const overrides = {
			name: {
				props: {
					value: this.scriptInfo?.name
				}
			},
			description: {
				props: {
					value: this.scriptInfo?.description
				}
			}
		};

		const data = await this.builderApiService.openFormlyDialog(
			{
				scriptId: 2,
				title: 'Script Info',
				overrides: overrides,
				clientOnly: true
			},
			this.dialog
		);

		if (data) {
			const updated = await this.builderApiService.saveScriptInfo({
				scriptId: this.scriptId,
				script: {
					scriptId: this.scriptId,
					description: data?.model?.description,
					name: data?.model?.name
				}
			});

			if (updated) {
				this.scriptInfo.name = data?.model?.name;
				this.scriptInfo.description = data?.model?.description;
				this.snackBar.open('Successfully saved', 'x', { duration: 3000 });
			}
		}
	}

	// Quick Add methods
	public onQuickAddSelected(event: MatAutocompleteSelectedEvent): void {
		const block = event.option.value as BlockType;
		if (this.selectedTabIndex === 2 && this.fieldEditor) {
			// JSON Editor tab - insert into JSON editor
			this.fieldEditor.blockTypeSelected(block);
		}
		this.quickAddControl.setValue('');
	}

	public filterQuickAdd(): void {
		const filterValue = this.quickAddInput?.nativeElement?.value?.toLowerCase() || '';
		this.filteredQuickAddOptions = of(
			this.quickAddBlocks.filter((b) => b.name.toLowerCase().includes(filterValue))
		);
	}

	private filterQuickAddBlocks(value: string): BlockType[] {
		if (typeof value === 'string') {
			const filterValue = value.toLowerCase();
			return this.quickAddBlocks.filter((b) => b.name.toLowerCase().includes(filterValue));
		}
		return this.quickAddBlocks;
	}

	public importFromTripetto(): void {
		const dialogRef = this.dialog.open(ImportFromTripettoDialogComponent, {
			width: '800px',
			maxHeight: '90vh',
			data: {}
		});

		dialogRef.afterClosed().subscribe((result: ConversionResult) => {
			if (result?.formlyConfig) {
				// Update fields with converted config
				this.fields = this.sanitizeFields(result.formlyConfig);
				this.scriptData = JSON.stringify(this.fields);

				// Update JSON editor content
				this.content = { json: JSON.parse(JSON.stringify(this.fields)) };
				if (this.fieldEditor?.editor) {
					this.fieldEditor.editor.set(this.content);
				}

				// Mark as changed
				this.hasChanges = true;

				// Log success message with warning information
				if (result.warnings.length > 0) {
					const warningCount = result.warnings.filter((w) => w.severity === 'warning').length;
					const errorCount = result.warnings.filter((w) => w.severity === 'error').length;

					let message = `Import successful. `;
					if (errorCount > 0) {
						message += `${errorCount} error(s), `;
					}
					if (warningCount > 0) {
						message += `${warningCount} warning(s). `;
					}
					message += `Please review the converted script.`;

					this.snackBar.open(message, 'OK', { duration: 5000 });
				} else {
					this.snackBar.open(
						`Successfully imported ${result.formlyConfig.length} field(s) from Tripetto.`,
						'OK',
						{ duration: 3000 }
					);
				}
			}
		});
	}
}
